import { useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Users, Heart, MessageCircle, ShieldCheck, TrendingUp, UserCheck, Crown, BarChart3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('admin_token');
const COLORS = ['#78716C', '#C0C0C0', '#F2A900', '#D9381E'];

export default function AdminDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/dashboard`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        setData(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-3 border-[#D9381E] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!data) return <p className="text-[#78716C]">Failed to load dashboard data</p>;

  const pieData = Object.entries(data.plan_distribution || {}).map(([name, value]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value }));

  const statCards = [
    { icon: Users, label: 'Total Users', value: data.total_users, color: 'text-[#D9381E]', bg: 'bg-[#D9381E]/10' },
    { icon: UserCheck, label: 'Active Users', value: data.active_users, color: 'text-green-600', bg: 'bg-green-100' },
    { icon: ShieldCheck, label: 'Verified Users', value: data.verified_users, color: 'text-blue-600', bg: 'bg-blue-100' },
    { icon: Heart, label: 'Total Interests', value: data.total_interests, color: 'text-[#F2A900]', bg: 'bg-[#F2A900]/10' },
    { icon: TrendingUp, label: 'Accepted Matches', value: data.accepted_interests, color: 'text-purple-600', bg: 'bg-purple-100' },
    { icon: MessageCircle, label: 'Total Messages', value: data.total_messages, color: 'text-teal-600', bg: 'bg-teal-100' },
    { icon: Users, label: 'Male Users', value: data.male_users, color: 'text-sky-600', bg: 'bg-sky-100' },
    { icon: Users, label: 'Female Users', value: data.female_users, color: 'text-pink-600', bg: 'bg-pink-100' },
  ];

  return (
    <div data-testid="admin-dashboard-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#2D2424]">Dashboard</h1>
        <p className="text-sm text-[#78716C]">Overview of VivahRang platform analytics</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((s, i) => (
          <Card key={i} className="p-4 rounded-xl border border-[#E7E5E4] bg-white" data-testid={`stat-card-${i}`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg ${s.bg} flex items-center justify-center`}>
                <s.icon className={`w-5 h-5 ${s.color}`} />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#2D2424]">{s.value}</div>
                <div className="text-xs text-[#78716C]">{s.label}</div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Daily Registrations */}
        <Card className="p-5 rounded-xl border border-[#E7E5E4] bg-white">
          <h3 className="text-sm font-semibold text-[#2D2424] mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-[#D9381E]" /> Daily Registrations (Last 7 Days)
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={data.daily_registrations || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#D9381E" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Plan Distribution */}
        <Card className="p-5 rounded-xl border border-[#E7E5E4] bg-white">
          <h3 className="text-sm font-semibold text-[#2D2424] mb-4 flex items-center gap-2">
            <Crown className="w-4 h-4 text-[#F2A900]" /> Subscription Distribution
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={3} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                {pieData.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-4 mt-2">
            {pieData.map((entry, idx) => (
              <div key={idx} className="flex items-center gap-1.5 text-xs">
                <div className="w-3 h-3 rounded-full" style={{ background: COLORS[idx % COLORS.length] }} />
                {entry.name}
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
